import { List } from "immutable"
import * as React from "react"
import { ButtonBlock, ButtonBlockProps } from "../components/button_block"
import { FaqBlock, FaqBlockProps } from "../components/faq_block"
import { HighlightBlock, HighlightBlockProps } from "../components/highlight_block"
import { ImageBlock, ImageBlockProps } from "../components/image_block"
import { Container } from "../components/library/container"
import { RichTextBlock, RichTextBlockProps } from "../components/rich_text_block"
import { VideoBlock, VideoBlockProps } from "../components/video_block"
import { ActivityClusterBlock, ActivityClusterBlockProps } from "./activity_cluster_block"
import { BooksBlock, BooksBlockProps } from "./books_block"
import { CollectionBlock, CollectionBlockProps } from "./collection_block"
import { ContentGridBlock, ContentGridBlockProps } from "./content_grid_block"
import { CarouselContentGridBlock, CarouselContentGridBlockProps } from "./carousel_content_grid_block"
import { FeaturedContentBlock, FeaturedContentBlockProps } from "./featured_content_block"
import { ImagesBlock, ImagesBlockProps } from "./images_block"
import { HarcodedSubscriptionBlock } from "./subscriptions_block"
import { VacancyOverviewBlock, VacancyOverviewBlockProps } from "./vacancy_overview_block"
import { AsyncBlockProps, AsyncBlock } from "./async_block"
import { QuoteBlockProps, QuoteBlock } from "./quote_block"
import { CollectionSearchBar, CollectionSearchBarProps } from "./collection_search_bar"
import { GridBlock, GridBlockProps } from "./grid_block"
import { TippedActivitiesBlock, TippedActivitiesBlockProps } from "./tipped_activities_block"
import { WordCloudBlock, WordCloudBlockProps } from "./word_cloud_block"
import { FormstackBlock, FormstackBlockProps } from "./formstack/formstack_block"
import { CtaBlock, CtaBlockProps } from "./cta_block"
import { NewsletterFormBlock } from "./newsletterFormBlock/newsletterFormBlockLayout"
import { NewsletterFormBlockProps } from "./newsletterFormBlock/newsletterFormBlockState"
import { SubMenuBlock, SubMenuProps } from "./submenu_block"
import { ThemakiezerBlockProps } from "./ThemakiezerBlock/ThemakiezerBlockState"
import { ThemakiezerBlock } from "./ThemakiezerBlock/ThemakiezerBlockWidget"

export type Block =
  | { kind: "richText" } & RichTextBlockProps
  | { kind: "image" } & ImageBlockProps
  | { kind: "images" } & ImagesBlockProps
  | { kind: "content_grid" } & ContentGridBlockProps
  | { kind: "carousel_content_grid"} & CarouselContentGridBlockProps
  | { kind: "async_block" } & AsyncBlockProps<any>
  | { kind: "video" } & VideoBlockProps
  | { kind: "faq" } & FaqBlockProps
  | { kind: "knop" } & ButtonBlockProps
  | { kind: "highlight" } & HighlightBlockProps
  | { kind: "vacancy_overview" } & VacancyOverviewBlockProps
  | { kind: "books" } & BooksBlockProps
  | { kind: "collection" } & CollectionBlockProps
  | { kind: "featured_content" } & FeaturedContentBlockProps
  | { kind: "activity_cluster" } & ActivityClusterBlockProps
  | { kind: "subscription_block" }
  | { kind: "quote" } & QuoteBlockProps
  | { kind: "collection_search_bar" } & CollectionSearchBarProps
  | { kind: "grid_block" } & GridBlockProps
  | { kind: "tipped_activities" } & TippedActivitiesBlockProps
  | { kind: "word_cloud_block" } & WordCloudBlockProps
  | { kind: "formstack_block" } & FormstackBlockProps
  | { kind: "cta_block" } & CtaBlockProps
  | { kind: "newsletterForm" } & NewsletterFormBlockProps
  | { kind: "submenu" } & SubMenuProps
  | { kind: "themakiezer" } & ThemakiezerBlockProps
  | { kind: "empty" }

interface BlockRendererProps {
  readonly blocks: List<Block>
}

interface BlockRendererState {}

export class BlockRenderer extends React.Component<BlockRendererProps, BlockRendererState> {
  constructor(props: BlockRendererProps) {
    super(props)

    this.state = {}
  }

  render() {
    const p = this.props
    if (p.blocks.isEmpty()) return <></>

    return (
      <>
        <Container>
          {List(p.blocks)
            .map((b, i, l) => (
              <React.Fragment key={`block-${i}`}>
                {generateBlock(b)}
                <hr className={`after-${b.kind} before-${l.get(i + 1)?.kind}`} />
              </React.Fragment>
            ))
            .toArray()}
        </Container>
      </>
    )
  }
}

export const generateBlock: (block: Block) => JSX.Element = b =>
  b.kind == "richText" ? (
    <RichTextBlock {...b} />
  ) : b.kind == "image" ? (
    <ImageBlock {...b} />
  ) : b.kind == "video" ? (
    <VideoBlock {...b} />
  ) : b.kind == "faq" ? (
    <FaqBlock {...b} />
  ) : b.kind == "highlight" ? (
    <HighlightBlock {...b} />
  ) : b.kind == "images" ? (
    <ImagesBlock {...b} />
  ) : b.kind == "content_grid" ? (
    <ContentGridBlock {...b} />
  ) : b.kind == "carousel_content_grid" ? (
    <CarouselContentGridBlock {...b} />
  ) : b.kind == "async_block" ? (
    <AsyncBlock {...b} />
  ) : b.kind == "knop" ? (
    <ButtonBlock {...b} />
  ) : b.kind == "subscription_block" ? (
    <HarcodedSubscriptionBlock />
  ) : b.kind == "vacancy_overview" ? (
    <VacancyOverviewBlock {...b} />
  ) : b.kind == "books" ? (
    <BooksBlock {...b} />
  ) : b.kind == "themakiezer" ? (
    <ThemakiezerBlock {...b} />
  ) : b.kind == "collection" ? (
    <CollectionBlock {...b} />
  ) : b.kind == "featured_content" ? (
    <FeaturedContentBlock {...b} />
  ) : b.kind == "activity_cluster" ? (
    <ActivityClusterBlock {...b} />
  ) : b.kind == "quote" ? (
    <QuoteBlock {...b} />
  ) : b.kind == "collection_search_bar" ? (
      <section className="search-wrapper">
        <CollectionSearchBar {...b} />
      </section>
  ) : b.kind == "grid_block" ? (
    <GridBlock {...b} />
  ) : b.kind == "tipped_activities" ? (
    <TippedActivitiesBlock {...b} />
  ) : b.kind == "word_cloud_block" ? (
    <WordCloudBlock {...b} />
  ) : b.kind == "formstack_block" ? (
    <FormstackBlock {...b} />
  ) : b.kind == "cta_block" ? (
    <CtaBlock {...b} />
  ) : b.kind == "newsletterForm" ? (
    <NewsletterFormBlock {...b} />
  ) : b.kind == "submenu" ? (
    <SubMenuBlock {...b} />
) : ((block) => { console.warn("no renderer found for:", block); return <></>})(b)
